import { c as _c } from "react/compiler-runtime";
import { useTheme } from 'next-themes';
import { Children, cloneElement, isValidElement, type ReactNode } from 'react';

// Monaco only colorizes inside a mounted editor with its theme active, so a read-only preview has to
// draw a token itself. These mirror `template-variable` in packages/ui/.../monaco-config.ts and
// `.template-variable-decoration` in styling-utils.ts, so the chip matches the field beside it.
const TOKEN_COLOR = {
  light: '#24585B',
  dark: '#C3B6F3'
} as const;
const CHIP_BACKGROUND = '#CBC1F640';

// The capturing group is what makes `split` keep each token as its own entry.
const TOKEN = /(\{\{[^}]*\}\})/;
const IS_TOKEN = /^\{\{[^}]*\}\}$/;
type TemplatedTextProps = {
  value: string;
};
const TemplatedText = t0 => {
  const $ = _c(7);
  const {
    value
  } = t0;
  const {
    resolvedTheme
  } = useTheme();
  const color = resolvedTheme === "dark" ? TOKEN_COLOR.dark : TOKEN_COLOR.light;
  let t1;
  if ($[0] !== color || $[1] !== value) {
    let t2;
    if ($[3] !== color) {
      t2 = (part, index) => IS_TOKEN.test(part) ? <code key={index} className="text-snippet-2 rounded px-0.5" style={{
        color,
        backgroundColor: CHIP_BACKGROUND
      }}>{part}</code> : part;
      $[3] = color;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = value.split(TOKEN).map(t2);
    $[0] = color;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <span>{t1}</span>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};

/** Highlights tokens in a markdown node's text, descending through nested markup like `**bold**`. */
const templatedChildren = (children: ReactNode): ReactNode => Children.map(children, child => {
  if (typeof child === 'string') {
    return <TemplatedText value={child} />;
  }
  if (isValidElement<{
    children?: ReactNode;
  }>(child)) {
    return cloneElement(child, undefined, templatedChildren(child.props.children));
  }
  return child;
});
export default TemplatedText;
export { templatedChildren };